// frontend/src/components/homepage/ClientsLive.js
//
// "בין לקוחותינו" - four real customers, each card a real link to their own
// site. This is the page's only third-party proof, so every part of it is
// literal: real names, real marks, real URLs, `target="_blank"` with
// `rel="noopener noreferrer"` on every one.
//
// STATIC ROW BY DEFAULT, MARQUEE AVAILABLE. Four logos fit a row, and a marquee
// of four reads as a page that cannot fill its own section - it advertises the
// shortness of the list rather than the list. The `variant` prop carries the
// marquee for the day the list outgrows the row (the owner's threshold is 8), so
// that switch is one prop rather than a redesign. The marquee half-and-travel
// rule is the ticker's, and homepage.css states it once for both.
//
// EMBOODO HAS NO MARK ON FILE. Its card renders the name as a WORDMARK in the
// page's display face, in the same card, at the same weight as its neighbours -
// not a dashed "logo pending" placeholder like the prototype's. A placeholder
// says the page is unfinished; this is a real customer and the card should say
// so. When the file arrives, adding `logo:` to its entry is the whole change.
//
// The heading is ONE constant per language (`HEADING`), because the owner has
// said he may reword it: a heading spread across markup is a heading nobody can
// find.

import React from "react";
import SectionShell from "./SectionShell";
import useReveal from "../../motion/useReveal";
import analytics, { CLIENT_LOGO_CLICK_EVENT } from "../../services/analytics";

/**
 * The customers, in display order.
 *
 * `logo` is a path under `public/`, or null for a name we have no mark for.
 * Every URL is the company's own canonical site.
 *
 * `slug` is the ONLY thing a click reports (see `trackClientLogoClick`). It
 * mirrors the backend's `CLIENT_LOGO_SLUGS` allowlist
 * (`backend/app/schemas/analytics.py`) in this same order - a new customer
 * needs its slug added THERE first, or its clicks 422 and count nothing. The
 * backend test `test_client_logo_click_event.py` reads this literal and pins
 * both the set and the order.
 */
export const CLIENTS = [
  { slug: "anyapp", name: "AnyApp", logo: "/clients/anyapp.svg", url: "https://any-app.com/" },
  {
    slug: "startplan",
    name: "StartPlan",
    logo: "/clients/startplan.svg",
    url: "https://startplan.net/",
  },
  {
    slug: "targo",
    name: "Targo",
    logo: "/clients/targo.png",
    url: "https://www.targo-consulting.co.il/",
  },
  {
    slug: "emboodo",
    name: "Emboodo",
    logo: null,
    // No mark on file yet, so the card SETS the brand's own wordmark instead of
    // showing a placeholder: uppercase, widely tracked, and the three-bar "E"
    // the company itself uses. `barInitial` drops the literal E and draws it,
    // because no font ships that glyph. Owner-supplied reference, 2026-09-03.
    wordmark: { text: "EMBOODO", barInitial: true },
    url: "https://www.emboodo.com/",
  },
];

/** The section heading - one constant per language, owner-reworded in place. */
export const HEADING = {
  he: {
    // Owner-chosen 2026-09-04: the eyebrow is the nav link's word, the title
    // carries the phrase. (Earlier: "עסקים ובעלי מקצוע שכבר עובדים עם סוכני
    // ה-AI שלנו".) The nowrap slot stays so the render shape is unchanged.
    eyebrow: "לקוחות",
    headingBefore: "בין לקוחותינו",
    headingNoWrap: "",
    headingAfter: "",
  },
  en: {
    eyebrow: "Clients",
    headingBefore: "Among our clients",
    headingNoWrap: "our AI agents",
    headingAfter: "",
  },
};

/**
 * The centred head block: a plain div, because it needs a ref and two class
 * names and nothing else. An `sx` object here would re-serialise on every
 * reveal transition to produce styling that is already static in homepage.css.
 */
function SectionHead({ headRef, className, children }) {
  return (
    <div
      ref={headRef}
      className={className ? `hl-head hl-head-center ${className}` : "hl-head hl-head-center"}
    >
      {children}
    </div>
  );
}

/**
 * A set wordmark for a client whose mark we have no file for.
 *
 * `barInitial` replaces the leading letter with three drawn rules - Emboodo's
 * own "E" is a three-bar glyph no font carries, and faking it with a Greek Ξ
 * would pull a different face's metrics into the middle of a word. Three CSS
 * rules on one box scale with the font size and cost nothing.
 *
 * aria-hidden because the anchor around it already carries the company name as
 * its accessible label, and the visible letters are one short of the word.
 */
function ClientWordmark({ wordmark }) {
  const { text, barInitial } = wordmark;
  return (
    <span className="hl-logo-wordmark" aria-hidden="true">
      {barInitial && <i className="hl-logo-wordmark-e" />}
      {barInitial ? text.slice(1) : text}
    </span>
  );
}

/**
 * Report one logo click - WHEN and WHICH, nothing else.
 *
 * Fire-and-forget on the anchor's `click`: the link opens in a new tab, so
 * the navigation does not wait on anything here, and the service's own post
 * is never awaited either. Failure-isolated like every instrumented call site
 * in the app (see services/analytics.js): a throwing tracker must never turn
 * a customer's link into a dead card.
 */
export function trackClientLogoClick(slug) {
  try {
    analytics.trackEvent(CLIENT_LOGO_CLICK_EVENT, { client: slug });
  } catch (error) {
    /* analytics must never break the link */
  }
}

/**
 * One customer card. Always an anchor, whether or not there is a mark to show -
 * the link is the point, and a card that is a link for three of four customers
 * and a dead div for the fourth is worse than either.
 */
function ClientCard({ client }) {
  return (
    <a
      className="hl-logo-card"
      href={client.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={client.name}
      onClick={() => trackClientLogoClick(client.slug)}
    >
      {client.logo ? (
        <img src={client.logo} alt={client.name} loading="lazy" decoding="async" />
      ) : (
        <ClientWordmark wordmark={client.wordmark || { text: client.name }} />
      )}
    </a>
  );
}

/**
 * @param {object} props
 * @param {boolean} [props.isHebrew]
 * @param {"static"|"marquee"} [props.variant] `marquee` once the list outgrows
 *   the row; the default row is what ships today.
 * @param {string} [props.note] one line under the heading, in the section
 *   sub register. There is NO default: the homepage passes nothing and renders
 *   no such line, exactly as it does today. A profession page uses it to say
 *   these customers come from other fields.
 */
export default function ClientsLive({ isHebrew = true, variant = "static", note }) {
  const t = isHebrew ? HEADING.he : HEADING.en;
  const head = useReveal();
  const body = useReveal({ threshold: 0.1 });

  const cards = CLIENTS.map((client) => <ClientCard key={client.name} client={client} />);

  return (
    <SectionShell
      id="clients"
      scene="clients"
      mesh="disperse"
      meshAlpha=".35"
      // prototype `.clients{padding:96px 0 72px}` - one rule, every width -
      // scaled by the owner's round-3 -20% (96 -> 76, 72 -> 58), because this
      // is the section he asked the tightening to START at.
      // `py: 0` first: it REPLACES the shell's responsive `py` (same key), whose
      // per-breakpoint media rules would otherwise outrank a bare pt/pb.
      sx={{ py: 0, pt: "76px", pb: "58px" }}
    >
      <SectionHead
        headRef={head.ref}
        className={head.className ? `hl-clients-head ${head.className}` : "hl-clients-head"}
      >
        <div className="hl-eyebrow">{t.eyebrow}</div>
        <h2 className="hl-h2 hl-h2-center">
          {t.headingBefore}
          <span style={{ whiteSpace: "nowrap" }}>{t.headingNoWrap}</span>
          {t.headingAfter}
        </h2>
        {note ? <p className="hl-sub hl-sub-center">{note}</p> : null}
      </SectionHead>

      {variant === "marquee" ? (
        // The track is the list TWICE for the same seamlessness reason the
        // ticker states: `hl-marquee` travels exactly half the track.
        // The reveal goes on the WRAPPER, never on the track: the track's
        // transform belongs to the marquee animation, and an animation beats a
        // transition, so a reveal there would silently do half its job.
        <div
          className={body.className ? `hl-logos ${body.className}` : "hl-logos"}
          ref={body.ref}
        >
          <div className="hl-logos-track">
            {cards}
            {CLIENTS.map((client) => (
              <ClientCard key={`dup:${client.name}`} client={client} />
            ))}
          </div>
        </div>
      ) : (
        <div
          className={body.className ? `hl-logo-row ${body.className}` : "hl-logo-row"}
          ref={body.ref}
        >
          {cards}
        </div>
      )}
    </SectionShell>
  );
}
